import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOutreachStats } from '../hooks/useOutreachHistory';
import { useMessages } from '../hooks/useSendBatches';
import ThreadViewModal from '../components/ThreadViewModal';

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    queued: 'bg-yellow-100 text-yellow-800',
    sending: 'bg-blue-100 text-blue-800',
    sent: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    opened: 'bg-purple-100 text-purple-800',
    replied: 'bg-indigo-100 text-indigo-800',
    bounced: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-600',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {status}
    </span>
  );
}

function StatCard({
  label,
  value,
  sub,
  color = 'text-gray-900',
}: {
  label: string;
  value: string | number;
  sub?: React.ReactNode;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'opened', label: 'Opened' },
  { value: 'replied', label: 'Replied' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'failed', label: 'Failed' },
  { value: 'queued', label: 'Queued' },
  { value: 'draft', label: 'Draft' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function OutreachDashboardPage() {
  const { data: stats } = useOutreachStats();
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const { data: messagesData, isLoading } = useMessages({
    status: statusFilter || undefined,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : undefined,
    page,
    limit: 25,
  });

  const pagination = messagesData?.pagination;

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Outreach History</h1>
        <p className="text-sm text-gray-500 mt-0.5">Track your sends, replies, and engagement</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label="Sent (7 days)"
          value={stats?.recentSendsCount ?? '—'}
          sub="emails sent this week"
        />
        <StatCard
          label="Reply Rate"
          value={stats ? `${stats.replyRate}%` : '—'}
          sub="last 30 days"
          color={
            stats && stats.replyRate >= 20
              ? 'text-green-600'
              : stats && stats.replyRate > 0
              ? 'text-yellow-600'
              : 'text-gray-900'
          }
        />
        <StatCard
          label="Bounce Rate"
          value={stats ? `${stats.bounceRate}%` : '—'}
          sub="last 30 days"
          color={stats && stats.bounceRate > 5 ? 'text-red-600' : 'text-gray-900'}
        />
        <StatCard
          label="Active Queues"
          value={stats?.activeQueuesCount ?? '—'}
          sub={
            stats && stats.activeQueuesCount > 0 ? (
              <Link to="/send-queue" className="text-blue-600 hover:underline">
                View queue →
              </Link>
            ) : undefined
          }
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {(statusFilter || dateFrom || dateTo) && (
            <button
              onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-gray-400 hover:text-gray-700 py-1.5"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Messages table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading && (
          <div className="py-12 text-center text-sm text-gray-400">Loading messages...</div>
        )}
        {!isLoading && messagesData?.messages.length === 0 && (
          <div className="py-12 text-center text-sm text-gray-400">No messages found.</div>
        )}
        {!isLoading && messagesData && messagesData.messages.length > 0 && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Contact
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Subject
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide hidden sm:table-cell">
                    Sent
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide hidden md:table-cell">
                    Opens
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {messagesData.messages.map((msg) => (
                  <tr
                    key={msg.id}
                    onClick={() => setSelectedMessageId(msg.id)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      {msg.contactFirstName || msg.contactLastName ? (
                        <span className="font-medium text-gray-900">
                          {msg.contactFirstName} {msg.contactLastName}
                        </span>
                      ) : (
                        <span className="text-gray-500">{msg.toEmail}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">
                      {msg.subject ?? <span className="text-gray-400 italic">no subject</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden sm:table-cell whitespace-nowrap">
                      {formatDate(msg.sentAt ?? msg.createdAt)}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {msg.openCount > 0 ? (
                        <span className="text-purple-600 font-medium">{msg.openCount}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={msg.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
                <span className="text-gray-500">
                  {pagination.total} messages · page {pagination.page} of {pagination.pages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedMessageId && (
        <ThreadViewModal
          messageId={selectedMessageId}
          onClose={() => setSelectedMessageId(null)}
        />
      )}
    </div>
  );
}
